import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {AgregarPaquetesDialog} from '@/components/bateria/agregar-paquetes-dialog'
import {AgregarPersonaDialog} from '@/components/bateria/agregar-persona-dialog'
import {ReasignarPaqueteDialog} from '@/components/bateria/reasignar-paquete-dialog'
import {RetirarPaqueteDialog} from '@/components/bateria/retirar-paquete-dialog'
import {CambiarFormaDialog} from '@/components/bateria/cambiar-forma-dialog'
import {CorregirIdentidadDialog} from '@/components/bateria/corregir-identidad-dialog'
import {PaginaHistorialDialog} from '@/components/bateria/pagina-historial-dialog'
import {ResultadoPaqueteDialog} from '@/components/bateria/resultado-paquete-dialog'
import {FichaDialog} from '@/components/bateria/ficha-dialog'
import {SubirEscaneosControl} from '@/components/bateria/subir-escaneos-control'
import type {BateriaPaquete} from '@/hooks/bateria/use-bateria'
import type {LotesResumen} from '@/lib/bateria'

/** El padre es el único dueño del estado; acá solo se renderizan y se cierran con `null`. */
export function PersonasDialogs({
  businessId,
  evaluacionId,
  paquetes,
  lotes,
  adding,
  onAddingChange,
  addingPersona,
  onAddingPersonaChange,
  retirando,
  onRetirandoChange,
  reasignando,
  onReasignandoChange,
  cambiandoForma,
  onCambiandoFormaChange,
  corrigiendoIdentidad,
  onCorrigiendoIdentidadChange,
  viendoHistorialPaginas,
  onViendoHistorialPaginasChange,
  reprocesando,
  onReprocesandoChange,
  viendoResultado,
  onViendoResultadoChange,
  viendoFicha,
  onViendoFichaChange,
}: {
  businessId: string
  evaluacionId: string
  paquetes: BateriaPaquete[]
  lotes: LotesResumen
  adding: boolean
  onAddingChange: (open: boolean) => void
  addingPersona: boolean
  onAddingPersonaChange: (open: boolean) => void
  retirando: BateriaPaquete[] | null
  onRetirandoChange: (next: null) => void
  reasignando: BateriaPaquete | null
  onReasignandoChange: (next: null) => void
  cambiandoForma: BateriaPaquete | null
  onCambiandoFormaChange: (next: null) => void
  corrigiendoIdentidad: BateriaPaquete | null
  onCorrigiendoIdentidadChange: (next: null) => void
  viendoHistorialPaginas: BateriaPaquete | null
  onViendoHistorialPaginasChange: (next: null) => void
  reprocesando: BateriaPaquete | null
  onReprocesandoChange: (next: null) => void
  viendoResultado: BateriaPaquete | null
  onViendoResultadoChange: (next: null) => void
  viendoFicha: BateriaPaquete | null
  onViendoFichaChange: (next: null) => void
}) {
  const {t} = useTranslation()
  return (
    <>
      <AgregarPaquetesDialog
        businessId={businessId}
        evaluacionId={evaluacionId}
        open={adding}
        onOpenChange={onAddingChange}
        existingEmployeeIds={new Set(paquetes.map((p) => p.employee_id))}
      />

      <AgregarPersonaDialog
        businessId={businessId}
        evaluacionId={evaluacionId}
        open={addingPersona}
        onOpenChange={onAddingPersonaChange}
      />

      {retirando && (
        <RetirarPaqueteDialog
          businessId={businessId}
          evaluacionId={evaluacionId}
          paquetes={retirando}
          open={!!retirando}
          onOpenChange={(open) => !open && onRetirandoChange(null)}
        />
      )}

      {reasignando && (
        <ReasignarPaqueteDialog
          businessId={businessId}
          evaluacionId={evaluacionId}
          paquete={reasignando}
          open={!!reasignando}
          onOpenChange={(open) => !open && onReasignandoChange(null)}
        />
      )}

      {cambiandoForma && (
        <CambiarFormaDialog
          businessId={businessId}
          evaluacionId={evaluacionId}
          paquete={cambiandoForma}
          open={!!cambiandoForma}
          onOpenChange={(open) => !open && onCambiandoFormaChange(null)}
        />
      )}

      {corrigiendoIdentidad && (
        <CorregirIdentidadDialog
          businessId={businessId}
          evaluacionId={evaluacionId}
          paquete={corrigiendoIdentidad}
          open={!!corrigiendoIdentidad}
          onOpenChange={(open) => !open && onCorrigiendoIdentidadChange(null)}
        />
      )}

      {viendoHistorialPaginas && (
        <PaginaHistorialDialog
          businessId={businessId}
          evaluacionId={evaluacionId}
          paquete={viendoHistorialPaginas}
          open={!!viendoHistorialPaginas}
          onOpenChange={(open) => !open && onViendoHistorialPaginasChange(null)}
        />
      )}

      {reprocesando && (
        <Dialog open onOpenChange={(open) => !open && onReprocesandoChange(null)}>
          <DialogContent data-testid="reprocesar-dialog">
            <DialogHeader>
              <DialogTitle>{t('admin.bateria.reprocesar.title', {nombre: reprocesando.employee_name})}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t('admin.bateria.reprocesar.intro')}</p>
              <SubirEscaneosControl
                businessId={businessId}
                evaluacionId={evaluacionId}
                paquete={reprocesando}
                lotes={lotes}
                variant="paquete"
                forzar
                label={t('admin.bateria.reprocesar.elegirArchivo')}
              />
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => onReprocesandoChange(null)}>
                  {t('common.close')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {viendoResultado && (
        <ResultadoPaqueteDialog
          businessId={businessId}
          paquete={viendoResultado}
          open={!!viendoResultado}
          onOpenChange={(open) => !open && onViendoResultadoChange(null)}
        />
      )}

      {viendoFicha && (
        <FichaDialog
          businessId={businessId}
          evaluacionId={evaluacionId}
          paquete={viendoFicha}
          open={!!viendoFicha}
          onOpenChange={(open) => !open && onViendoFichaChange(null)}
        />
      )}
    </>
  )
}
